import { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, X } from 'lucide-react';
import api from '../api/axios';
import PageHeader from '../components/layout/PageHeader';
import ConfirmDialog from '../components/shared/ConfirmDialog';
import { ROLES } from '../utils/constants';

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [assemblies, setAssemblies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'VIEWER', assembly_id: '' });

  const fetchData = () => {
    Promise.all([
      api.get('/users'),
      api.get('/assemblies'),
    ]).then(([uRes, aRes]) => {
      setUsers(uRes.data);
      setAssemblies(aRes.data);
    }).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, []);

  const openAdd = () => { setForm({ name: '', email: '', password: '', role: 'VIEWER', assembly_id: '' }); setModal('add'); };
  const openEdit = (u) => { setForm({ name: u.name, email: u.email, password: '', role: u.role, assembly_id: u.assembly_id || '' }); setModal(u); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      if (!payload.password) delete payload.password;
      if (!payload.assembly_id) payload.assembly_id = null;

      if (modal === 'add') {
        if (!form.password) return alert('Password is required');
        await api.post('/users', payload);
      } else {
        await api.put(`/users/${modal.id}`, payload);
      }
      setModal(null);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.error || 'Operation failed');
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/users/${deleteId}`);
      setDeleteId(null);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.error || 'Delete failed');
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin w-8 h-8 border-4 border-teal-500 border-t-transparent rounded-full" /></div>;

  return (
    <div>
      <PageHeader title="Users" subtitle="Manage user accounts">
        <button onClick={openAdd} className="flex items-center gap-1 px-4 py-2 bg-teal-600 text-white rounded-lg text-sm hover:bg-teal-700">
          <Plus className="w-4 h-4" /> Add User
        </button>
      </PageHeader>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-[700px] w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left py-3 px-4 text-gray-500 font-medium">Name</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium">Email</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium">Role</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium">Assembly</th>
                <th className="text-right py-3 px-4 text-gray-500 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-3 px-4 font-medium">{u.name}</td>
                  <td className="py-3 px-4 text-gray-600">{u.email}</td>
                  <td className="py-3 px-4"><span className="px-2 py-0.5 bg-gray-100 rounded text-xs font-medium">{ROLES[u.role] || u.role}</span></td>
                  <td className="py-3 px-4 text-gray-600">{u.assembly_name || 'All (Super Admin)'}</td>
                  <td className="py-3 px-4 text-right">
                    <button onClick={() => openEdit(u)} className="p-1.5 rounded hover:bg-gray-100 text-gray-500"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => setDeleteId(u.id)} className="p-1.5 rounded hover:bg-red-50 text-red-500 ml-1"><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {modal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">{modal === 'add' ? 'Add User' : 'Edit User'}</h3>
              <button onClick={() => setModal(null)}><X className="w-5 h-5 text-gray-400" /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-3">
              <input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Full Name" required className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              <input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="Email" required disabled={modal !== 'add'} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-100" />
              <input type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} placeholder={modal === 'add' ? 'Password' : 'New password (leave empty to keep)'} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              <select value={form.role} onChange={e => setForm({...form, role: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                <option value="SUPER_ADMIN">Super Admin</option>
                <option value="AC_ADMIN">AC Admin</option>
                <option value="VIEWER">Viewer</option>
              </select>
              {form.role !== 'SUPER_ADMIN' && (
                <select value={form.assembly_id} onChange={e => setForm({...form, assembly_id: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                  <option value="">Select Assembly</option>
                  {assemblies.map(a => <option key={a.id} value={a.id}>{a.name} ({a.code})</option>)}
                </select>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setModal(null)} className="px-4 py-2 border border-gray-300 rounded-lg text-sm">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm hover:bg-teal-700">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!deleteId}
        title="Delete User"
        message="Are you sure you want to delete this user?"
        onConfirm={handleDelete}
        onCancel={() => setDeleteId(null)}
      />
    </div>
  );
}
